import { AccountLabel } from "@/entities/Labeling/Account.model";
import { useCallback, useEffect, useMemo } from "react";
import { MetricQuery, WindowOperation } from "./metrics.types";
import { useRouter } from "@/utils/router";
import { parseMetricQuery, serializeMetricQuery } from "./metricQuery.utils";
import { useMetricStack } from "./useMetricStack";

export function useMetricQuery() {
    const router = useRouter();
    const queryString = router.search["metric-query"] as string | undefined;
    const { appendToStack, stack } = useMetricStack();

    const currentMetricQuery = useMemo(
        () => parseMetricQuery(queryString),
        [queryString],
    );

    useEffect(() => {
        if (!stack[0] || queryString !== serializeMetricQuery(stack[0])) {
            appendToStack(currentMetricQuery);
        }
    }, [queryString, appendToStack, stack]);

    useEffect(() => {
        if (!queryString) {
            router.updateSearch("metric-query", "revenues-y");
        }
    }, [currentMetricQuery, router, queryString]);

    const updateQuery = useCallback(
        (update: Partial<Omit<MetricQuery, "metricQuery">>) => {
            const updated = { ...currentMetricQuery, ...update };
            const serialized = serializeMetricQuery(updated);
            router.updateSearch("metric-query", serialized);
        },
        [currentMetricQuery, router],
    );

    const addWindow = useCallback(
        (window: MetricQuery["window"]) => {
            updateQuery({ window });
        },
        [updateQuery],
    );

    const setWindowSize = useCallback(
        (size: number) => {
            if (!currentMetricQuery.window) {
                return;
            }
            updateQuery({
                window: { ...currentMetricQuery.window, size },
            });
        },
        [currentMetricQuery.window, updateQuery],
    );

    const setWindowApplyLast = useCallback(
        (applyLast: boolean) => {
            if (!currentMetricQuery.window) {
                return;
            }
            updateQuery({
                window: { ...currentMetricQuery.window, applyLast },
            });
        },
        [currentMetricQuery.window, updateQuery],
    );

    const setWindowOperation = useCallback(
        (operation: WindowOperation) => {
            if (!currentMetricQuery.window) {
                return;
            }
            updateQuery({
                window: { ...currentMetricQuery.window, operation },
            });
        },
        [currentMetricQuery.window, updateQuery],
    );

    const removeWindow = useCallback(() => {
        updateQuery({ window: undefined });
    }, [updateQuery]);

    const setMetric = useCallback(
        (metric: AccountLabel) => {
            updateQuery({ metric });
        },
        [updateQuery],
    );

    const setPeriodResolution = useCallback(
        (periodResolution: "m" | "q" | "y") => {
            updateQuery({ periodResolution });
        },
        [updateQuery],
    );

    const setWindow = useCallback(
        (window: MetricQuery["window"]) => {
            updateQuery({ window });
        },
        [updateQuery],
    );

    const setCurrentMetricQuery = useCallback(
        (query: Omit<MetricQuery, "metricQuery">) => {
            const serialized = serializeMetricQuery(query);
            router.updateSearch("metric-query", serialized);
        },
        [router],
    );

    return {
        addWindow,
        removeWindow,
        setMetric,
        setPeriodResolution,
        setWindow,
        setWindowSize,
        setWindowApplyLast,
        setWindowOperation,
        setCurrentMetricQuery,
        currentMetricQuery,
    };
}
